import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { BASE_API_URL } from '../helpers/Config';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { TriggerBalanceUpdate } from '../helpers/TriggerBalanceUpdate';

const NewStakingScreen = ({ theme = 'dark' }) => {
    const isDarkMode = theme === 'dark';
    const [amount, setAmount] = useState('');
    const [selectedAmount, setSelectedAmount] = useState(null);
    const [UsdtBalance, setUsdtBalance] = useState(localStorage.getItem('usdt_balance') || 0);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isStaking, setIsStaking] = useState(false);

    const navigate = useNavigate();

    // Theme colors
    const themeColors = {
        dark: {
            background: '#121826',
            surface: '#1E293B',
            surfaceLight: '#2D3748',
            primary: '#8B5CF6',
            primaryHover: '#7C3AED',
            text: '#E5E7EB',
            textSecondary: '#9CA3AF',
            border: '#374151',
            icon: '#9CA3AF',
            shadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            success: '#10B981',
            warning: '#F59E0B',
            info: '#3B82F6',
            error: '#EF4444'
        },
        light: {
            background: '#F9FAFB',
            surface: '#FFFFFF',
            surfaceLight: '#F3F4F6',
            primary: '#7C3AED',
            primaryHover: '#6D28D9',
            text: '#1F2937',
            textSecondary: '#6B7280',
            border: '#E5E7EB',
            icon: '#6B7280',
            shadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            success: '#059669',
            warning: '#D97706',
            info: '#2563EB',
            error: '#DC2626'
        }
    };

    const colors = isDarkMode ? themeColors.dark : themeColors.light;

    // Staking plans table data - compact version
    const stakingPlans = [
        { name: 'Lite', months: 18, min: 100, max: 1499, color: colors.info },
        { name: 'Plus', months: 17, min: 1500, max: 3499, color: colors.primary },
        { name: 'Pro', months: 16, min: 3500, max: 6999, color: colors.warning },
        { name: 'Max', months: 15, min: 7000, max: '∞', color: colors.success }
    ];

    const FetchProfile = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
                return;
            }

            if (data?.data) {
                setUsdtBalance(data?.data?.usdt)
                TriggerBalanceUpdate({ usdt: data?.data?.usdt, balance: data?.data?.balance })
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        FetchProfile();
    }, []);

    // Suggested amounts - 8 values in 2 rows
    const suggestedAmounts = [100, 200, 500, 1000, 2000, 5000, 7000, 10000];

    // Get staking plan based on amount
    const getStakingPlan = (amt) => {
        const numAmount = parseInt(amt) || 0;

        if (numAmount >= 100 && numAmount <= 1499) {
            return stakingPlans[0];
        } else if (numAmount >= 1500 && numAmount <= 3499) {
            return stakingPlans[1];
        } else if (numAmount >= 3500 && numAmount <= 6999) {
            return stakingPlans[2];
        } else if (numAmount >= 7000) {
            return stakingPlans[3];
        } else {
            return { name: 'Select Amount', months: 0, color: colors.textSecondary };
        }
    };

    // Calculate returns
    const calculateReturns = (amt) => {
        const numAmount = parseInt(amt) || 0;
        const plan = getStakingPlan(numAmount);

        if (numAmount < 100 || plan.months === 0) return null;

        const totalReturn = numAmount * 2;
        const monthlyReturn = totalReturn / plan.months;
        const monthlyPercentage = (monthlyReturn / numAmount) * 100;
        const totalProfit = totalReturn - numAmount;
        const capitalPortion = numAmount / plan.months;
        const profitPortion = totalProfit / plan.months;

        return {
            totalReturn,
            monthlyReturn,
            monthlyPercentage,
            totalProfit,
            capitalPortion,
            profitPortion,
            plan
        };
    };

    const returns = calculateReturns(amount);
    const plan = getStakingPlan(amount);
    const inputAmount = parseInt(amount) || 0;

    // Reset all states
    const resetStates = () => {
        setAmount('');
        setSelectedAmount(null);
        setIsStaking(false);
        setShowConfirmModal(false);
    };

    // Handle amount input - only integers
    const handleAmountChange = (e) => {
        let value = e.target.value;
        // Remove any non-digit characters
        value = value.replace(/[^\d]/g, '');
        // Parse to integer and set
        setAmount(value);
        setSelectedAmount(null);
    };

    // Handle max button click
    const handleMaxClick = () => {
        // Get integer value of wallet balance
        const maxAmount = Math.floor(UsdtBalance);
        setAmount(maxAmount.toString());
        setSelectedAmount(maxAmount);
    };

    // Handle suggested amount
    const handleSuggestedAmount = (amt) => {
        setAmount(amt.toString());
        setSelectedAmount(amt);
    };

    // Show confirmation modal
    const handleStakeClick = () => {
        if (inputAmount >= 100) {
            if (inputAmount > UsdtBalance) {
                toast.error('Insufficient wallet balance!');
                return;
            }
            setShowConfirmModal(true);
        }
    };

    // Handle actual staking
    const handleStakeConfirm = async () => {
        setIsStaking(true);
        try {
            const response = await axios.post(`${BASE_API_URL}/staking/create`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                amount: Number(amount)
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
                setIsStaking(false);
                return;
            }

            if (data?.msg) {
                toast.success(data?.msg);
                FetchProfile();
                resetStates();
                // Navigate to my-staking page after 1 second
                setTimeout(() => {
                    navigate("/my-staking");
                }, 1000);
            }
        } catch (error) {
            toast.error('Technical error');
            setIsStaking(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US').format(value);
    };

    // Animation variants
    const pageVariants = {
        initial: { opacity: 0 },
        animate: {
            opacity: 1,
            transition: { duration: 0.3 }
        }
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 }
    };

    // Custom styles
    const styles = `
        .staking-calc-container {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            user-select: none;
            max-width: 500px;
            margin: 0 auto;
            -webkit-tap-highlight-color: transparent;
        }
        
        /* Modal Styles */
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
        }
        
        .modal-content {
            background: ${colors.surface};
            border-radius: 16px;
            padding: 24px;
            max-width: 400px;
            width: 100%;
            border: 1px solid ${colors.border};
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }
        
        .modal-header {
            text-align: center;
            margin-bottom: 20px;
        }
        
        .modal-icon {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background: ${colors.primary}20;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 28px;
            color: ${colors.primary};
            margin: 0 auto 16px;
        }
        
        .modal-title {
            font-size: 20px;
            font-weight: 700;
            color: ${colors.text};
            margin-bottom: 8px;
        }
        
        .modal-subtitle {
            font-size: 14px;
            color: ${colors.textSecondary};
            line-height: 1.4;
        }
        
        .modal-details {
            background: ${colors.surfaceLight};
            border-radius: 12px;
            padding: 16px;
            margin-bottom: 24px;
            border: 1px solid ${colors.border};
        }
        
        .detail-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
        }
        
        .detail-label {
            font-size: 13px;
            color: ${colors.textSecondary};
        }
        
        .detail-value {
            font-size: 14px;
            font-weight: 600;
            color: ${colors.text};
        }
        
        .modal-actions {
            display: flex;
            gap: 12px;
        }
        
        .modal-button {
            flex: 1;
            padding: 14px;
            border-radius: 10px;
            font-weight: 600;
            font-size: 15px;
            border: none;
            cursor: pointer;
            transition: all 0.2s ease;
        }
        
        .modal-cancel {
            background: ${colors.surface};
            color: ${colors.text};
            border: 1px solid ${colors.border};
        }
        
        .modal-confirm {
            background: ${colors.primary};
            color: white;
        }
        
        .modal-confirm:disabled {
            opacity: 0.7;
            cursor: not-allowed;
        }
        
        /* Compact Plans Table */
        .compact-plans-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 6px;
            margin-bottom: 16px;
        }
        
        .plan-compact-card {
            padding: 10px 6px;
            border-radius: 8px;
            background: ${colors.surface};
            border: 1px solid ${colors.border};
            text-align: center;
            min-height: 70px;
            display: flex;
            flex-direction: column;
            justify-content: center;
        }
        
        .plan-name-compact {
            font-size: 12px;
            font-weight: 700;
            margin-bottom: 4px;
        }
        
        .plan-months-compact {
            font-size: 11px;
            color: ${colors.textSecondary};
            margin-bottom: 2px;
        }
        
        .plan-range-compact {
            font-size: 10px;
            color: ${colors.textSecondary}80;
        }
        
        /* Amount Input with Max Button */
        .amount-input-wrapper {
            position: relative;
            margin-bottom: 14px;
        }
        
        .amount-input {
            width: 100%;
            padding: 12px 80px 12px 40px;
            font-size: 20px;
            font-weight: 700;
            text-align: center;
            border: 1px solid ${colors.border};
            border-radius: 10px;
            background: ${colors.surface};
            color: ${colors.text};
            outline: none;
            transition: all 0.2s ease;
            height: 50px;
            box-sizing: border-box;
        }
        
        .amount-input:focus {
            border-color: ${colors.primary};
            box-shadow: 0 0 0 2px ${colors.primary}20;
        }
        
        .currency-symbol {
            position: absolute;
            left: 14px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 16px;
            font-weight: 600;
            color: ${colors.text};
            opacity: 0.7;
        }
        
        .max-button {
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            padding: 4px 10px;
            border-radius: 6px;
            font-size: 11px;
            font-weight: 700;
            background: ${colors.primary};
            color: white;
            border: none;
            cursor: pointer;
            transition: all 0.2s ease;
        }
        
        .max-button:hover {
            background: ${colors.primaryHover};
        }
        
        /* Quick Amounts - 4 per row */
        .suggested-amounts {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 6px;
            margin-bottom: 16px;
        }
        
        .amount-chip {
            padding: 10px 4px;
            border-radius: 8px;
            font-weight: 600;
            font-size: 12px;
            cursor: pointer;
            transition: all 0.2s ease;
            border: 1px solid ${colors.border};
            background: ${colors.surface};
            color: ${colors.text};
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .amount-chip:hover {
            transform: translateY(-1px);
        }
        
        .amount-chip.selected {
            background: ${colors.primary};
            color: white;
            border-color: ${colors.primary};
        }
        
        /* Returns Grid - 2 items per row */
        .returns-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
            margin-bottom: 14px;
        }
        
        .return-item-compact {
            padding: 12px 10px;
            border-radius: 8px;
            background: ${colors.surface};
            border: 1px solid ${colors.border};
            text-align: center;
        }
        
        .return-label {
            font-size: 11px;
            color: ${colors.textSecondary};
            margin-bottom: 4px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        
        .return-value {
            font-size: 16px;
            font-weight: 700;
            color: ${colors.text};
            margin-bottom: 2px;
        }
        
        .return-percentage {
            font-size: 10px;
            color: ${colors.primary};
            font-weight: 600;
            background: ${colors.primary}15;
            padding: 2px 6px;
            border-radius: 10px;
            display: inline-block;
        }
        
        /* Commission Breakdown Grid */
        .commission-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
            margin-bottom: 14px;
        }
        
        .commission-item {
            padding: 10px 8px;
            border-radius: 8px;
            background: ${colors.surfaceLight};
            border: 1px solid ${colors.border};
            text-align: center;
        }
        
        .commission-label {
            font-size: 10px;
            color: ${colors.textSecondary};
            margin-bottom: 4px;
        }
        
        .commission-value {
            font-size: 14px;
            font-weight: 700;
            color: ${colors.text};
        }
        
        /* Plan Badge */
        .plan-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 700;
            margin-bottom: 10px;
        }
        
        /* Warning Messages */
        .warning-message {
            padding: 10px;
            border-radius: 8px;
            background: ${colors.warning}10;
            border: 1px solid ${colors.warning}30;
            margin-bottom: 12px;
            text-align: center;
        }
        
        .insufficient-balance {
            padding: 10px;
            border-radius: 8px;
            background: ${colors.error}10;
            border: 1px solid ${colors.error}30;
            margin-bottom: 12px;
            text-align: center;
        }
        
        /* Compact Terms */
        .terms-section {
            margin-top: 16px;
            padding: 12px;
            border-radius: 8px;
            background: ${colors.surfaceLight};
            border: 1px solid ${colors.border};
        }
        
        .terms-title {
            font-size: 12px;
            font-weight: 700;
            color: ${colors.text};
            margin-bottom: 8px;
            display: flex;
            align-items: center;
        }
        
        .terms-icon {
            margin-right: 6px;
            font-size: 12px;
        }
        
        .terms-list {
            padding-left: 16px;
            margin-bottom: 8px;
        }
        
        .terms-item {
            font-size: 10px;
            color: ${colors.textSecondary};
            line-height: 1.3;
            margin-bottom: 4px;
        }
        
        .terms-note {
            font-size: 9px;
            color: ${colors.textSecondary}80;
            font-style: italic;
            line-height: 1.3;
            padding-top: 8px;
            border-top: 1px solid ${colors.border}50;
        }
        
        /* Header and Balance */
        .wallet-balance-display {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
        }
        
        .wallet-label {
            font-size: 8px;
            color: ${colors.textSecondary};
            margin-bottom: 1px;
        }
        
        .wallet-amount {
            font-size: 14px;
            font-weight: 700;
            color: ${colors.primary};
        }
        
        /* Stake Button */
        .stake-button {
            width: 100%;
            padding: 14px;
            border-radius: 10px;
            font-weight: 700;
            font-size: 14px;
            border: none;
            cursor: pointer;
            transition: all 0.2s ease;
            background: ${colors.primary};
            color: white;
            margin-top: 12px;
        }
        
        .stake-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
        
        .stake-button:not(:disabled):hover {
            background: ${colors.primaryHover};
            transform: translateY(-1px);
        }
        
        /* Info Note */
        .info-note {
            font-size: 10px;
            color: ${colors.textSecondary};
            text-align: center;
            margin-top: 12px;
            padding: 8px;
            border-radius: 6px;
            background: ${colors.surfaceLight};
            border: 1px solid ${colors.border}50;
        }
        
        /* Responsive */
        @media (max-width: 380px) {
            .compact-plans-grid {
                grid-template-columns: repeat(2, 1fr);
                gap: 8px;
            }
            
            .suggested-amounts {
                grid-template-columns: repeat(4, 1fr);
            }
            
            .plan-compact-card {
                min-height: 65px;
                padding: 8px 4px;
            }
            
            .plan-name-compact {
                font-size: 11px;
            }
            
            .plan-range-compact {
                font-size: 9px;
            }
        }
        
        @media (max-width: 320px) {
            .suggested-amounts {
                grid-template-columns: repeat(2, 1fr);
            }
            
            .returns-grid {
                grid-template-columns: 1fr;
            }
            
            .commission-grid {
                grid-template-columns: 1fr;
            }
            
            .modal-content {
                padding: 20px 16px;
            }
            
            .modal-actions {
                flex-direction: column;
            }
        }
    `;

    return (
        <>
            <motion.div
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="staking-calc-container"
                style={{
                    minHeight: '100vh',
                    padding: '12px 10px',
                    background: colors.background,
                    color: colors.text,
                    transition: 'background 0.25s ease, color 0.25s ease'
                }}
            >
                <style>{styles}</style>

                <div className="container-fluid px-0">
                    {/* Header - Compact */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                            <h1 style={{
                                fontWeight: 700,
                                fontSize: '18px',
                                color: colors.text,
                                margin: 0
                            }}>
                                New Recovery Trade
                            </h1>
                            <p style={{
                                color: colors.textSecondary,
                                fontSize: '10px',
                                margin: '2px 0 0 0',
                                opacity: 0.8
                            }}>
                                Enter amount to Recovery Trade
                            </p>
                        </div>
                        <div className="wallet-balance-display">
                            <div className="wallet-label">Available</div>
                            <div className="wallet-amount">${UsdtBalance}</div>
                        </div>
                    </div>

                    {/* Compact Plans Grid */}
                    <div style={{ fontSize: '11px', fontWeight: 600, color: colors.textSecondary, marginBottom: '8px' }}>
                        Recovery Trade Plans
                    </div>
                    <div className="compact-plans-grid">
                        {stakingPlans.map((planItem, index) => (
                            <div key={index} className="plan-compact-card" style={{ borderColor: planItem.color + '40' }}>
                                <div className="plan-name-compact" style={{ color: planItem.color }}>
                                    {planItem.name}
                                </div>
                                <div className="plan-months-compact">
                                    {planItem.months} months
                                </div>
                                <div className="plan-range-compact">
                                    ${planItem.min} - {planItem.max === '∞' ? '∞' : '$' + formatCurrency(planItem.max)}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Amount Input with Max Button */}
                    <div className="amount-input-wrapper">
                        <div className="currency-symbol">$</div>
                        <input
                            type="text"
                            value={amount}
                            onChange={handleAmountChange}
                            placeholder="0"
                            className="amount-input"
                            inputMode="numeric"
                        />
                        <button
                            className="max-button"
                            onClick={handleMaxClick}
                            type="button"
                        >
                            MAX
                        </button>
                    </div>

                    {/* Warnings */}
                    {amount && inputAmount < 100 && (
                        <div className="warning-message">
                            <div style={{ fontSize: '10px', fontWeight: 600, color: colors.warning }}>
                                Minimum: $100
                            </div>
                        </div>
                    )}

                    {amount && inputAmount >= 100 && inputAmount > UsdtBalance && (
                        <div className="insufficient-balance">
                            <div style={{ fontSize: '10px', fontWeight: 600, color: colors.error }}>
                                Insufficient Balance
                            </div>
                            <div style={{ fontSize: '9px', color: colors.textSecondary }}>
                                Available: ${UsdtBalance}
                            </div>
                        </div>
                    )}

                    {/* Quick Amounts - 8 items in 2 rows */}
                    <div style={{ fontSize: '11px', fontWeight: 600, color: colors.textSecondary, marginBottom: '8px' }}>
                        Quick Amounts
                    </div>
                    <div className="suggested-amounts">
                        {suggestedAmounts.map((amt) => (
                            <div
                                key={amt}
                                className={`amount-chip ${selectedAmount === amt ? 'selected' : ''}`}
                                onClick={() => handleSuggestedAmount(amt)}
                            >
                                ${formatCurrency(amt)}
                            </div>
                        ))}
                    </div>

                    {/* Selected Plan Badge */}
                    {amount && inputAmount >= 100 && returns && (
                        <>
                            <div className="plan-badge" style={{
                                background: plan.color + '20',
                                color: plan.color
                            }}>
                                {plan.name} Plan • {plan.months} Months
                            </div>

                            {/* Returns Grid - 2 items per row */}
                            <div className="returns-grid">
                                <div className="return-item-compact">
                                    <div className="return-label">Your Investment</div>
                                    <div className="return-value">${formatCurrency(inputAmount)}</div>
                                </div>

                                <div className="return-item-compact">
                                    <div className="return-label">Total Return (2x)</div>
                                    <div className="return-value" style={{ color: colors.success }}>
                                        ${formatCurrency(returns.totalReturn)}
                                    </div>
                                </div>

                                <div className="return-item-compact">
                                    <div className="return-label">Monthly Return</div>
                                    <div className="return-value">${formatCurrency(Math.round(returns.monthlyReturn))}</div>
                                    <div className="return-percentage">
                                        {returns.monthlyPercentage.toFixed(2)}%
                                    </div>
                                </div>

                                <div className="return-item-compact">
                                    <div className="return-label">Monthly %</div>
                                    <div className="return-value" style={{ color: colors.primary }}>
                                        {((100 / plan.months) * 2).toFixed(2)}%
                                    </div>
                                    <div style={{ fontSize: '9px', color: colors.textSecondary }}>
                                        of deposit
                                    </div>
                                </div>
                            </div>

                            {/* Commission Breakdown Grid */}
                            <div style={{ fontSize: '11px', fontWeight: 600, color: colors.textSecondary, marginBottom: '8px' }}>
                                Monthly Commission Breakdown
                            </div>
                            <div className="commission-grid">
                                <div className="commission-item">
                                    <div className="commission-label">50% From Capital</div>
                                    <div className="commission-value" style={{ color: colors.info }}>
                                        ${formatCurrency(Math.round(returns.capitalPortion))}
                                    </div>
                                </div>

                                <div className="commission-item">
                                    <div className="commission-label">50% From Profit</div>
                                    <div className="commission-value" style={{ color: colors.success }}>
                                        ${formatCurrency(Math.round(returns.profitPortion))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Stake Button */}
                    <button
                        onClick={handleStakeClick}
                        disabled={!amount || inputAmount < 100 || inputAmount > UsdtBalance}
                        className="stake-button"
                    >
                        {inputAmount >= 100 ? `R-Trade $${formatCurrency(inputAmount)}` : 'Enter $100+ to R-Trade'}
                    </button>

                    {/* Compact Terms */}
                    <div className="terms-section">
                        <div className="terms-title">
                            <span className="terms-icon">📝</span>
                            Terms & Conditions
                        </div>
                        <ul className="terms-list">
                            <li className="terms-item">Min: $100 • Max: Based on plan</li>
                            <li className="terms-item">Monthly commissions paid equally from capital & profit</li>
                            <li className="terms-item">No early withdrawals • 2x total return</li>
                            <li className="terms-item">By recovery trading, you agree to all terms</li>
                        </ul>
                    </div>

                    {/* Info Note */}
                    <div className="info-note">
                        ⚡ Recovery paid monthly. Initial capital recovered after term.
                    </div>
                </div>
            </motion.div>

            {/* Confirmation Modal */}
            <AnimatePresence>
                {showConfirmModal && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => !isStaking && setShowConfirmModal(false)}
                    >
                        <motion.div
                            className="modal-content"
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <div className="modal-title">Confirm Recovery Trade</div>
                            </div>

                            <div className="modal-details">
                                <div className="detail-row">
                                    <span className="detail-label">R-Trade Amount</span>
                                    <span className="detail-value">${formatCurrency(inputAmount)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">R-Trade Plan</span>
                                    <span className="detail-value" style={{ color: plan.color }}>
                                        {plan.name} Plan ({plan.months} months)
                                    </span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Total Recovery</span>
                                    <span className="detail-value" style={{ color: colors.success }}>
                                        ${formatCurrency(returns?.totalReturn || 0)}
                                    </span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Monthly Recovery</span>
                                    <span className="detail-value">
                                        ${formatCurrency(Math.round(returns?.monthlyReturn || 0))}
                                    </span>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button
                                    className="modal-button modal-cancel"
                                    onClick={() => setShowConfirmModal(false)}
                                    disabled={isStaking}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="modal-button modal-confirm"
                                    onClick={handleStakeConfirm}
                                    disabled={isStaking}
                                >
                                    {isStaking ? 'Processing...' : 'Confirm & R-Trade'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default NewStakingScreen;